import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { programsAPI, coachesAPI, studentsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { Plus, Loader2, BookOpen, Users, Clock, Award, Edit, Trash2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

const Programs = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [expandedProgram, setExpandedProgram] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    total_lessons: 10,
    duration_weeks: 10,
    level: 'beginner',
    coach_ids: [],
    certificate_enabled: true,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [programsRes, coachesRes, studentsRes] = await Promise.all([
        programsAPI.getAll(),
        coachesAPI.getAll({}),
        studentsAPI.getAll({}),
      ]);
      setPrograms(programsRes.data);
      setCoaches(coachesRes.data);
      setStudents(studentsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Please enter a program name');
      return;
    }

    setSubmitting(true);
    try {
      if (selectedProgram) {
        await programsAPI.update(selectedProgram.id, formData);
        toast.success('Program updated successfully');
      } else {
        await programsAPI.create(formData);
        toast.success('Program created successfully');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save program');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedProgram) return;
    setSubmitting(true);
    try {
      await programsAPI.delete(selectedProgram.id);
      toast.success('Program deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedProgram(null);
      loadData();
    } catch (error) {
      toast.error('Failed to delete program');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      total_lessons: 10,
      duration_weeks: 10,
      level: 'beginner',
      coach_ids: [],
      certificate_enabled: true,
    });
    setSelectedProgram(null);
  };

  const openEditDialog = (program) => {
    setSelectedProgram(program);
    setFormData({
      name: program.name,
      description: program.description || '',
      total_lessons: program.total_lessons,
      duration_weeks: program.duration_weeks,
      level: program.level,
      coach_ids: program.coach_ids || [],
      certificate_enabled: program.certificate_enabled,
    });
    setDialogOpen(true);
  };

  const getProgramStudents = (programId) => {
    return students.filter(s => s.program_id === programId);
  };

  const getProgramCoaches = (programId) => {
    const program = programs.find(p => p.id === programId);
    if (!program) return [];
    return coaches.filter(c => program.coach_ids?.includes(c.id));
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'beginner': return 'bg-emerald-100 text-emerald-700';
      case 'intermediate': return 'bg-amber-100 text-amber-700';
      case 'advanced': return 'bg-red-100 text-red-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Programs</h1>
          <p className="text-slate-500">Manage swim training programs</p>
        </div>
        {isAdmin() && (
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="btn-primary" data-testid="add-program-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Program
          </Button>
        )}
      </div>

      {/* Programs List */}
      {programs.length === 0 ? (
        <div className="text-center py-12">
          <BookOpen className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="text-slate-400">No programs found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {programs.map((program) => (
            <Collapsible
              key={program.id}
              open={expandedProgram === program.id}
              onOpenChange={(open) => setExpandedProgram(open ? program.id : null)}
            >
              <Card className="overflow-hidden" data-testid={`program-card-${program.id}`}>
                <CollapsibleTrigger className="w-full">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                          <BookOpen className="w-6 h-6" />
                        </div>
                        <div className="text-left">
                          <h3 className="font-semibold text-slate-900">{program.name}</h3>
                          <div className="flex items-center gap-3 mt-1">
                            <Badge className={getLevelColor(program.level)}>
                              {program.level}
                            </Badge>
                            <span className="text-sm text-slate-500 flex items-center gap-1">
                              <Users className="w-4 h-4" />
                              {program.student_count} students
                            </span>
                            <span className="text-sm text-slate-500 flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {program.total_lessons} lessons
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {program.certificate_enabled && (
                          <Badge variant="outline" className="hidden sm:flex items-center gap-1">
                            <Award className="w-3 h-3" />
                            Certificate
                          </Badge>
                        )}
                        {isAdmin() && (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => { e.stopPropagation(); openEditDialog(program); }}
                              data-testid={`edit-program-${program.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => { e.stopPropagation(); setSelectedProgram(program); setDeleteDialogOpen(true); }}
                              className="text-destructive"
                              data-testid={`delete-program-${program.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${expandedProgram === program.id ? 'rotate-180' : ''}`} />
                      </div>
                    </div>
                  </CardContent>
                </CollapsibleTrigger>
                
                <CollapsibleContent>
                  <div className="border-t px-6 py-4 bg-slate-50">
                    {program.description && (
                      <p className="text-sm text-slate-600 mb-4">{program.description}</p>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Assigned Coaches */}
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900 mb-2">Assigned Coaches</h4>
                        <div className="space-y-2">
                          {getProgramCoaches(program.id).length === 0 ? (
                            <p className="text-sm text-slate-400">No coaches assigned</p>
                          ) : (
                            getProgramCoaches(program.id).map((coach) => (
                              <div key={coach.id} className="flex items-center gap-2 p-2 bg-white rounded-lg">
                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-semibold">
                                  {coach.name.split(' ').map(n => n[0]).join('')}
                                </div>
                                <span className="text-sm text-slate-700">{coach.name}</span>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                      
                      {/* Enrolled Students */}
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900 mb-2">Enrolled Students</h4>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {getProgramStudents(program.id).length === 0 ? (
                            <p className="text-sm text-slate-400">No students enrolled</p>
                          ) : (
                            getProgramStudents(program.id).map((student) => (
                              <div
                                key={student.id}
                                className="flex items-center justify-between p-2 bg-white rounded-lg cursor-pointer hover:bg-slate-100"
                                onClick={() => navigate(`/students/${student.id}`)}
                              >
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-full bg-secondary/20 flex items-center justify-center text-secondary text-xs font-semibold">
                                    {student.name.split(' ').map(n => n[0]).join('')}
                                  </div>
                                  <span className="text-sm text-slate-700">{student.name}</span>
                                </div>
                                <span className="text-xs text-slate-500">{Math.round(student.progress_percent)}%</span>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedProgram ? 'Edit Program' : 'Add New Program'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Program Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Learn to Swim - Beginners"
                data-testid="program-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Program description..."
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Total Lessons</Label>
                <Input
                  type="number"
                  value={formData.total_lessons}
                  onChange={(e) => setFormData({ ...formData, total_lessons: parseInt(e.target.value) })}
                  min={1}
                />
              </div>
              <div className="space-y-2">
                <Label>Duration (weeks)</Label>
                <Input
                  type="number"
                  value={formData.duration_weeks}
                  onChange={(e) => setFormData({ ...formData, duration_weeks: parseInt(e.target.value) })}
                  min={1}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Level</Label>
              <Select
                value={formData.level}
                onValueChange={(value) => setFormData({ ...formData, level: value })}
              >
                <SelectTrigger data-testid="program-level-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="beginner">Beginner</SelectItem>
                  <SelectItem value="intermediate">Intermediate</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="certificate"
                checked={formData.certificate_enabled}
                onChange={(e) => setFormData({ ...formData, certificate_enabled: e.target.checked })}
                className="rounded border-slate-300"
              />
              <Label htmlFor="certificate" className="cursor-pointer">Enable certificate on completion</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="submit-program-btn">
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {selectedProgram ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Program</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedProgram?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Programs;
